import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { DeleteAccountPopup } from './DeleteAccountPopup'
import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { PhotoSelector } from '@/components/PhotoSelector/PhotoSelector'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '@/components/ui/select'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import {
  useUpdateUserDataMutation,
  useUserInfoQuery,
} from '@/store/api/Users/Users.query'
import type { profileType } from '@/utils/schemas'
import { profileSchema } from '@/utils/schemas'

export const Account = () => {
  const {
    t,
    i18n: { changeLanguage, language },
  } = useTranslation()
  const navigate = useNavigate()

  const { data, isLoading: isLoadingUserData } = useUserInfoQuery()
  const [updateUserData, { isLoading: isLoadingUpdateData }] =
    useUpdateUserDataMutation()

  const [selectedLanguage, setSelectedLanguage] = useState<string>(language)

  const form = useForm<profileType>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: data?.firstname,
      lastName: data?.lastname,
      mail: data?.email,
      phoneNumber: data?.phoneNumber,
    },
    mode: 'onChange',
  })
  const { handleSubmit, control } = form

  const handleUpdateUserData = async () => {
    try {
      await updateUserData({
        language: selectedLanguage,
      }).unwrap()
      toast.success(t('feedback.toast.changesSaved'))
    } catch (error) {
      toast.error(t('feedback.toast.errorSavingChanges'))
      catchWithSentry(error)
    }
  }

  const onSubmit = () => {
    changeLanguage(selectedLanguage)
    handleUpdateUserData()
  }

  const handleLanguageChange = (newLanguage: string) => {
    setSelectedLanguage(newLanguage)
  }

  if (isLoadingUserData) {
    return <LottieSpinner width={320} height={320} text={t('common.loading')} />
  }

  return (
    <div>
      <div className="flex flex-row gap-4 items-center mb-3">
        {data && (
          <PhotoSelector
            initialImageUrl={data?.profilePicUrl}
            fullname={data?.firstname + ' ' + data?.lastname}
          />
        )}
      </div>

      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <p className="text-neutralColor text-xl font-medium tracking-wide font-satoshi mb-4">
            {t('dashboard.settings.languageTitle')}
          </p>
          <p className="text-neutralColor80 text-sm font-medium mb-2">
            {t('dashboard.settings.languageContent')}
          </p>
          <div className="flex gap-2 mb-4">
            <Select onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-full lg:w-2/6 rounded-3xl border border-neutralColor30 h-11">
                {selectedLanguage === 'es'
                  ? t('dashboard.settings.spanish')
                  : t('dashboard.settings.english')}
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel className="h-[34px] px-2 py-1.5 text-neutralColor text-base font-medium tracking-tight">
                    {t('dashboard.settings.selectLanguage')}
                  </SelectLabel>
                  <SelectItem value="es">
                    {t('dashboard.settings.spanish')}
                  </SelectItem>
                  <SelectItem value="en">
                    {t('dashboard.settings.english')}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col ">
            <p className="text-neutralColor text-xl font-semibold tracking-wide font-satoshi mb-4">
              {t('dashboard.settings.yourDetails')}
            </p>
            <div className="flex lg:flex-row flex-col max-md:gap-2 lg:justify-between mb-6">
              <FormField
                disabled={true}
                control={control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="lg:w-[48%] w-full">
                    <FormLabel className="text-neutralColor80 text-sm font-medium tracking-tight">
                      {t('dashboard.settings.firstName')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={data?.firstname}
                        className="rounded-3xl"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                disabled={true}
                control={control}
                name="lastName"
                rules={{ required: false }}
                render={({ field }) => (
                  <FormItem className="lg:w-[48%] w-full">
                    <FormLabel className="text-neutralColor80 text-sm font-medium tracking-tight">
                      {t('dashboard.settings.lastName')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={data?.lastname}
                        className="rounded-3xl"
                      />
                    </FormControl>
                    <FormMessage data-name="lastName" />
                  </FormItem>
                )}
              />
            </div>
            <p className="text-neutralColor text-xl font-semibold tracking-wide font-satoshi mb-4">
              {t('dashboard.settings.contactInfo')}
            </p>
            <div className="flex lg:flex-row flex-col lg:justify-between mb-6 max-md:gap-2 items-center">
              <FormField
                disabled={true}
                control={control}
                defaultValue={data?.email}
                name="mail"
                rules={{ required: false }}
                render={({ field }) => (
                  <FormItem className="lg:w-[48%] w-full">
                    <FormLabel className="text-sm font-medium tracking-tight text-neutralColor80">
                      {t('dashboard.settings.email')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={data?.email}
                        className="rounded-3xl"
                      />
                    </FormControl>
                    <FormMessage data-name="mail" />
                  </FormItem>
                )}
              />
              <FormField
                disabled={true}
                control={control}
                defaultValue={data?.phoneNumber}
                name="phoneNumber"
                rules={{ required: false }}
                render={({ field }) => (
                  <FormItem className="lg:w-[48%] w-full flex flex-col">
                    <div className="flex justify-between items-center">
                      <FormLabel className="text-sm font-medium tracking-tight text-neutralColor80">
                        {t('dashboard.settings.phoneNumber')}
                      </FormLabel>
                      {data?.phoneNumberVerified ? (
                        <span className="text-sm font-medium text-green-600">
                          ✓ {t('dashboard.settings.verified')}
                        </span>
                      ) : null}
                    </div>
                    <div className="w-full">
                      {data?.phoneNumberVerified ? (
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={data?.phoneNumber}
                            className="rounded-3xl"
                          />
                        </FormControl>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          className="whitespace-nowrap max-xl:w-full"
                          onClick={() =>
                            navigate(AppRoutes.settingsVerifyPhone)
                          }
                        >
                          {t('dashboard.settings.verifyPhoneNumber')}
                        </Button>
                      )}
                    </div>
                    <FormMessage data-name="phoneNumber" />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex lg:justify-end lg:items-end">
            <Button
              type="submit"
              className="mt-6 w-full lg:w-[75px] px-4 py-2 h-[38px]"
              isLoading={isLoadingUpdateData}
              disabled={language === selectedLanguage}
            >
              {t('dashboard.settings.save')}
            </Button>
          </div>
        </form>
      </Form>
      <div className="h-[0.5px] border-neutralColor30 bg-neutralColor30 my-10 max-md:my-5" />
      <div className="flex justify-between gap-4 items-center max-md:flex-col max-md:items-start">
        <div className="flex flex-col gap-2">
          <p className="text-dynamicRed text-xl font-semibold tracking-wide">
            {t('dashboard.settings.deleteAccount')}
          </p>
          <p className="text-neutralColor80 text-sm font-medium">
            {t('dashboard.settings.deleteAccountDescription')}
          </p>
        </div>
        <DeleteAccountPopup />
      </div>
    </div>
  )
}
